/**
 * Wheel palette — senior-readable, selectable-density wheel rings.
 *
 * Per Research B § 4:
 *   - 12 and 18 sectors remain available as comparison densities.
 *   - 24 sectors is the selected default: the radial vertical labels remain
 *     readable while giving the wheel the strongest game-show cadence.
 *   - The 24-sector ring preserves the original 12-sector gameplay odds
 *     exactly.
 *
 * Per BUILDER-PLAYBOOK rule #2 (color-blind safety): every wedge pairs its
 * colour with one of six iconographic shapes. No two adjacent wedges share
 * both colour AND shape — even with full deuteranopia the player can read
 * the ring.
 *
 * Per Research B's audience tuning: bias the palette warmer (more reds /
 * mustards / golds, less cool blue / green). The aged lens reads warm hues
 * more comfortably.
 */
import type { Wedge, WedgeKind } from './types';

// ---------------------------------------------------------------------------
// Warm-tilted Okabe-Ito-derived colours
// ---------------------------------------------------------------------------

/**
 * Eight base wedge fills. Drawn from Okabe-Ito (color-blind safe) then warm-
 * shifted ~10-15° toward red/orange per the brand-tension target.
 */
export const WEDGE_FILLS = {
  crimson: '#D94F45',
  orange: '#F39A32',
  mustard: '#E6C84C',
  sage: '#79B96C',
  slate: '#3E8FCC',
  magenta: '#D86A9D',
  plum: '#7657B8',
  cream: '#FFF1D1',
} as const;

/** Deep walnut for the Bankrupt face. */
export const BANKRUPT_FILL = '#15100D';
/** Warm gray for Lose a Turn — not punishing. */
export const LOSE_TURN_FILL = '#67616D';
/** Deep crimson for the top-dollar wedge. */
export const TOP_DOLLAR_FILL = '#A9162D';
/** Brushed warm gold used for the rim + top-dollar trim. */
export const RIM_GOLD = '#D3A63F';
/** Highlight gold for the moving specular accent on the rim. */
export const RIM_GOLD_HI = '#FFE08A';

// ---------------------------------------------------------------------------
// Wedge layout
// ---------------------------------------------------------------------------

interface WedgeSpec {
  kind: WedgeKind;
  value: number;
  fill: string;
  ink: string;
  shape: Wedge['shape'];
}

const C = WEDGE_FILLS;
const INK_LIGHT = '#F4ECDD'; // cream on dark wedges
const INK_DARK = '#3A2614'; // walnut on warm wedges

/**
 * The 24-wedge ring, clockwise from the 12-o'clock pointer (`index 0`).
 *
 * Layout principles:
 *   - Bankrupt wedges sit ADJACENT to the highest-value wedges. The show
 *     does this for visual jeopardy; we follow.
 *   - Each $5,000 wedge is flanked by Bankrupt.
 *   - The original cash / hazard / Free Play odds are preserved exactly.
 *   - No two adjacent wedges share BOTH colour AND shape.
 */
const RING_24: ReadonlyArray<WedgeSpec> = [
  { kind: 'cash', value: 900, fill: C.mustard, ink: INK_DARK, shape: 'circle' },
  { kind: 'cash', value: 650, fill: C.sage, ink: INK_DARK, shape: 'triangle' },
  { kind: 'cash', value: 1200, fill: C.slate, ink: INK_LIGHT, shape: 'circle' },
  { kind: 'lose_turn', value: 0, fill: LOSE_TURN_FILL, ink: INK_LIGHT, shape: 'dot' },
  { kind: 'cash', value: 800, fill: C.orange, ink: INK_DARK, shape: 'diamond' },
  { kind: 'cash', value: 2500, fill: C.plum, ink: INK_LIGHT, shape: 'star' },
  { kind: 'bankrupt', value: 0, fill: BANKRUPT_FILL, ink: INK_LIGHT, shape: 'diamond' },
  { kind: 'cash', value: 5000, fill: TOP_DOLLAR_FILL, ink: '#F2D27A', shape: 'star' },
  { kind: 'bankrupt', value: 0, fill: BANKRUPT_FILL, ink: INK_LIGHT, shape: 'diamond' },
  { kind: 'cash', value: 950, fill: C.mustard, ink: INK_DARK, shape: 'triangle' },
  { kind: 'free_play', value: 600, fill: C.cream, ink: INK_DARK, shape: 'star' },
  { kind: 'cash', value: 750, fill: C.crimson, ink: INK_LIGHT, shape: 'square' },
  { kind: 'cash', value: 900, fill: C.slate, ink: INK_LIGHT, shape: 'circle' },
  { kind: 'cash', value: 650, fill: C.sage, ink: INK_DARK, shape: 'triangle' },
  { kind: 'cash', value: 1500, fill: C.magenta, ink: INK_LIGHT, shape: 'circle' },
  { kind: 'lose_turn', value: 0, fill: LOSE_TURN_FILL, ink: INK_LIGHT, shape: 'dot' },
  { kind: 'cash', value: 800, fill: C.orange, ink: INK_DARK, shape: 'square' },
  { kind: 'cash', value: 2500, fill: C.plum, ink: INK_LIGHT, shape: 'star' },
  { kind: 'bankrupt', value: 0, fill: BANKRUPT_FILL, ink: INK_LIGHT, shape: 'square' },
  { kind: 'cash', value: 5000, fill: TOP_DOLLAR_FILL, ink: '#F2D27A', shape: 'star' },
  { kind: 'bankrupt', value: 0, fill: BANKRUPT_FILL, ink: INK_LIGHT, shape: 'diamond' },
  { kind: 'cash', value: 950, fill: C.mustard, ink: INK_DARK, shape: 'triangle' },
  { kind: 'free_play', value: 600, fill: C.cream, ink: INK_DARK, shape: 'star' },
  { kind: 'cash', value: 750, fill: C.crimson, ink: INK_LIGHT, shape: 'square' },
];

export const WHEEL_SECTOR_COUNTS = [12, 18, 24] as const;
export type WheelSectorCount = (typeof WHEEL_SECTOR_COUNTS)[number];

/** Selected production density. The study control can still compare 12 and 18. */
export const DEFAULT_WHEEL_SECTOR_COUNT: WheelSectorCount = 24;

export const WHEEL_SECTOR_OPTIONS: ReadonlyArray<{
  count: WheelSectorCount;
  label: string;
  description: string;
}> = [
  { count: 12, label: '12', description: 'Clearest labels' },
  { count: 18, label: '18', description: 'Balanced' },
  { count: 24, label: '24', description: 'Show-style' },
];

const RING_12 = RING_24.slice(0, 12);

/**
 * The midpoint ring keeps the 12-sector cash odds, slightly softens hazards,
 * and adds one extra Free Play. This makes it useful for visual comparison
 * without making the denser option more punishing for our audience.
 */
const RING_18 = [0, 1, 10, 2, 3, 4, 5, 6, 7, 8, 9, 22, 11, 12, 13, 14, 20, 17].map(
  (index) => RING_24[index]!,
);

const RINGS: Record<WheelSectorCount, ReadonlyArray<WedgeSpec>> = {
  12: RING_12,
  18: RING_18,
  24: RING_24,
};

export function parseWheelSectorCount(value: string | number | null): WheelSectorCount {
  const parsed = typeof value === 'number' ? value : Number(value);
  return WHEEL_SECTOR_COUNTS.includes(parsed as WheelSectorCount)
    ? (parsed as WheelSectorCount)
    : DEFAULT_WHEEL_SECTOR_COUNT;
}

function labelFor(spec: WedgeSpec): string {
  switch (spec.kind) {
    case 'cash':
      return '$' + spec.value.toLocaleString();
    case 'bankrupt':
      return 'BANKRUPT';
    case 'lose_turn':
      return 'LOSE A TURN';
    case 'free_play':
      return 'FREE PLAY';
  }
}

/**
 * Build a stable ring for tests and comparison tools. Engines and renderers
 * share the selected module-level instance so a wedge index always identifies
 * the same wedge for the life of a game.
 */
export function buildWheelWedges(count: WheelSectorCount): ReadonlyArray<Wedge> {
  return RINGS[count].map((spec, index) => ({
    index,
    kind: spec.kind,
    value: spec.value,
    label: labelFor(spec),
    fill: spec.fill,
    ink: spec.ink,
    shape: spec.shape,
  }));
}

function activeWheelSectorCount(): WheelSectorCount {
  if (typeof window === 'undefined') return DEFAULT_WHEEL_SECTOR_COUNT;
  return parseWheelSectorCount(new URLSearchParams(window.location.search).get('sectors'));
}

export const WHEEL_SECTOR_COUNT = activeWheelSectorCount();
export const WEDGES = buildWheelWedges(WHEEL_SECTOR_COUNT);

export const WEDGE_COUNT = WEDGES.length;
export const WEDGE_DEG = 360 / WEDGE_COUNT;

// ---------------------------------------------------------------------------
// Page palette — referenced by the React shell
// ---------------------------------------------------------------------------

/**
 * Puzzle board colours. Mirrors the show's dark green face / cream reveal,
 * shifted slightly warmer to sit on our editorial page.
 */
export const BOARD = {
  hiddenFace: '#1B3A2C', // deep forest green (the iconic unrevealed face)
  hiddenFaceDark: '#0F261C', // even deeper in dark mode
  revealedFace: '#F4ECDD', // warm cream
  revealedInk: '#1F1814', // near-black walnut
  spaceFace: 'rgba(244, 236, 221, 0.06)', // faint warm marker for word gaps
  frame: '#C9A24B', // warm brushed gold frame
  frameDark: '#8B7332',
} as const;

/**
 * Page tokens — the cream paper / lamp-amber backdrop the playbook
 * establishes for every editorial surface.
 */
export const PAGE = {
  paperLight: '#F4ECDD',
  paperDark: '#1F1814',
  inkLight: '#2A1F18',
  inkDark: '#E8DCC8',
  walnut: '#3D2817',
  gold: '#C9A24B',
  goldHi: '#F2D27A',
} as const;
